import { useEffect, useId, useState } from 'react';
import { KeyRoundIcon } from 'lucide-react';
import { AuthError, changeAccountPassword, fetchPasswordStatus } from '@/server-threads';
import {
  fieldHelpClass,
  phoneSafeInputTextClass,
  primaryCtaClass,
  textInputClass,
} from '@/components/ui/form-control';
import { cn } from '@/lib/utils';
import { PasswordInput } from '@/components/ui/password-input';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { passwordStringsFor } from '@/components/auth/password-strings';
import { namespaceCopyFor } from '@/i18n/ns';
import entryCopy from '@/i18n/ns/entry';

// Focus comes from the one field focus (ui/workspace/fields.css), never a utility ring.
const inputClass = cn(textInputClass, 'min-h-10 rounded-sm px-3', phoneSafeInputTextClass);
const ctaClass = cn(primaryCtaClass, 'min-h-10 rounded-lg px-4');

export function PasswordChangeSection({
  disabled,
  onChanged,
}: {
  disabled: boolean;
  onChanged: () => void;
}) {
  const locale = useChatInterfaceLocale();
  const copy = passwordStringsFor(locale);
  const entry = namespaceCopyFor(entryCopy, locale);
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const newPasswordHelpId = useId();

  useEffect(() => {
    let active = true;
    void fetchPasswordStatus()
      .then((value) => active && setHasPassword(value))
      .catch(() => active && setError(copy.errors.server_unavailable));
    return () => {
      active = false;
    };
  }, []);

  const submit = async () => {
    if (newPassword !== confirmation) {
      setError(copy.errors.password_mismatch);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await changeAccountPassword(currentPassword, newPassword);
      setComplete(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmation('');
    } catch (value) {
      const code = value instanceof AuthError ? value.code : 'generic';
      if (code === 'password_unavailable') setHasPassword(false);
      setError(copy.errors[code] ?? copy.errors.generic);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border-border border-l pl-4">
      <div className="flex items-start gap-3">
        <span className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-full">
          <KeyRoundIcon className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{copy.changeTitle}</div>
          <p className="text-muted-foreground mt-0.5 text-xs leading-snug">
            {complete
              ? copy.changeCompleteBody
              : hasPassword === false
                ? copy.passwordUnavailableBody
                : copy.changeBody}
          </p>
        </div>
      </div>

      {complete ? (
        <button type="button" onClick={onChanged} className={cn(ctaClass, 'mt-4')}>
          {copy.continueToLogin}
        </button>
      ) : hasPassword === null ? (
        <div className="text-muted-foreground mt-4 text-xs">{copy.checkingPassword}</div>
      ) : hasPassword ? (
        <div className="mt-4 flex flex-col gap-3">
          <PasswordInput
            aria-label={copy.currentPassword}
            autoComplete="current-password"
            placeholder={copy.currentPassword}
            value={currentPassword}
            disabled={disabled || busy}
            onChange={(event) => setCurrentPassword((event.target as HTMLInputElement).value)}
            className={inputClass}
            showLabel={copy.showPassword}
            hideLabel={copy.hidePassword}
          />
          {/* The rule is helper text under the field, not part of the
              placeholder, so it stays whole at 16 px on phones (§6.2). */}
          <div className="flex flex-col gap-1.5">
            <PasswordInput
              aria-label={entry.newPasswordLabel}
              aria-describedby={newPasswordHelpId}
              autoComplete="new-password"
              placeholder={entry.newPasswordLabel}
              value={newPassword}
              disabled={disabled || busy}
              onChange={(event) => setNewPassword((event.target as HTMLInputElement).value)}
              className={inputClass}
              showLabel={copy.showPassword}
              hideLabel={copy.hidePassword}
            />
            <p id={newPasswordHelpId} className={fieldHelpClass}>
              {entry.passwordHelp}
            </p>
          </div>
          <PasswordInput
            aria-label={copy.confirmPassword}
            autoComplete="new-password"
            placeholder={copy.confirmPassword}
            value={confirmation}
            disabled={disabled || busy}
            onChange={(event) => setConfirmation((event.target as HTMLInputElement).value)}
            onKeyDown={(event) => event.key === 'Enter' && void submit()}
            className={inputClass}
            showLabel={copy.showPassword}
            hideLabel={copy.hidePassword}
          />
          {error ? (
            <div role="alert" className="text-destructive text-xs">
              {error}
            </div>
          ) : null}
          <button
            type="button"
            disabled={disabled || busy}
            onClick={() => void submit()}
            className={cn(ctaClass, 'self-start')}
          >
            {busy ? '…' : copy.changePassword}
          </button>
        </div>
      ) : null}
    </section>
  );
}
